import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Typography, Button, Card, CardContent } from '@mui/material';
import ContactDialog from '../../components/shared/ContactDialog';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import DiamondRoundedIcon from '@mui/icons-material/DiamondRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import { selectLicense, selectPlanDefinitions, selectActivePromotion } from '../../store/actions/licenseActions';
import { auth } from '../../firebase';

const PLAN_CARDS = [
    {
        key: 'basic',
        Icon: BoltRoundedIcon,
        iconBg: 'rgba(217,119,6,0.1)',
        iconColor: '#D97706',
        price: '₪72/חודש',
        features: ['מסך ציבורי אחד', '2 מערכי תצוגה למסך', '2 לוחות למערך תצוגה', 'כל הרכיבים ללא הגבלה', 'תמיכה תוך 5 ימי עסקים'],
    },
    {
        key: 'pro',
        Icon: DiamondRoundedIcon,
        iconBg: 'rgba(30,58,95,0.12)',
        iconColor: '#1e3a5f',
        price: '₪101/חודש',
        features: ['עד 3 מסכים', '5 מערכי תצוגה למסך', '5 לוחות למערך תצוגה', 'כל הרכיבים ללא הגבלה', 'היסטוריית 5 גרסאות לכל לוח', 'ספריית רכיבים שמורים', 'תמיכה תוך 48 שעות'],
        highlight: true,
    },
    {
        key: 'community',
        Icon: GroupsRoundedIcon,
        iconBg: 'rgba(16,185,129,0.1)',
        iconColor: '#059669',
        price: 'בואו נדבר',
        features: ['מסכים ללא הגבלה', 'מערכי תצוגה ולוחות ללא הגבלה', 'גרסאות ורכיבים שמורים ללא הגבלה', 'הדרכה ואונבורדינג אישי', 'מנהל חשבון ייעודי', 'SLA מוגדר + תמיכה ישירה'],
    },
];

const UpgradeScreen = () => {
    const license = useSelector(selectLicense);
    const planDefs = useSelector(selectPlanDefinitions);
    const promo = useSelector(selectActivePromotion);

    const isTrialExpired = license?.plan === 'trial';
    const HeaderIcon = isTrialExpired ? EmojiEventsRoundedIcon : AutorenewRoundedIcon;

    const [contactOpen, setContactOpen] = useState(false);

    const handleLogout = async () => {
        try {
            await auth.signOut();
            window.location.href = '/';
        } catch (e) {
            window.location.href = '/';
        }
    };

    return (
        <>
        <Box sx={{
            minHeight: '100vh',
            background: 'linear-gradient(160deg, #0f2443 0%, #1e3a5f 55%, #1a3354 100%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            direction: 'rtl',
            p: 4,
            gap: 4,
            overflowY: 'auto',
        }}>
            {/* Header */}
            <Box sx={{ textAlign: 'center', maxWidth: 560 }}>
                <Box sx={{
                    width: 68,
                    height: 68,
                    borderRadius: '20px',
                    background: 'rgba(255,255,255,0.1)',
                    border: '1px solid rgba(255,255,255,0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2.5,
                }}>
                    <HeaderIcon sx={{ color: '#FCD34D', fontSize: 36 }} />
                </Box>
                <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '1.9rem', mb: 1.5, lineHeight: 1.3 }}>
                    {isTrialExpired
                        ? 'תקופת ההתנסות הסתיימה — תודה שהיית איתנו!'
                        : 'הרישיון פג תוקף — נשמח לחדש יחד'}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '1.05rem', lineHeight: 1.75 }}>
                    {isTrialExpired
                        ? 'קיבלת 14 יום להכיר את המערכת — מקווים שהייתה לך חוויה טובה! נשמח לשמוע מה חשבת ולמצוא יחד את המסלול שמתאים לכם.'
                        : 'אנחנו כאן בשבילכם ונשמח לסייע בחידוש המנוי. צרו קשר ונסדר את הכל בהקדם ממש.'}
                </Typography>
            </Box>

            {/* Active Promotion */}
            {promo && (
                <Box sx={{
                    background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                    border: '1.5px solid #6EE7B7',
                    borderRadius: '16px',
                    px: 4,
                    py: 2.5,
                    textAlign: 'center',
                    maxWidth: 440,
                    width: '100%',
                }}>
                    <Typography sx={{ fontWeight: 700, color: '#065F46', fontSize: '1rem' }}>
                        {promo.badgeText || '🎉 מבצע מיוחד'}
                    </Typography>
                    {promo.description && (
                        <Typography sx={{ color: '#047857', fontSize: '0.875rem', mt: 0.5 }}>
                            {promo.description}
                        </Typography>
                    )}
                </Box>
            )}

            {/* Plan Cards */}
            <Box sx={{
                display: 'flex',
                gap: 2.5,
                flexWrap: 'wrap',
                justifyContent: 'center',
                maxWidth: 880,
                width: '100%',
            }}>
                {PLAN_CARDS.map(plan => {
                    const displayName = planDefs?.[plan.key]?.displayName || plan.key;
                    const { Icon } = plan;
                    return (
                        <Card key={plan.key} sx={{
                            width: 248,
                            borderRadius: '20px',
                            bgcolor: '#fff',
                            flexShrink: 0,
                            boxShadow: plan.highlight
                                ? '0 12px 40px rgba(0,0,0,0.28)'
                                : '0 2px 16px rgba(0,0,0,0.14)',
                            transform: plan.highlight ? 'scale(1.05)' : 'none',
                            border: plan.highlight ? '2px solid #FCD34D' : '1px solid rgba(255,255,255,0.06)',
                            position: 'relative',
                            overflow: 'visible',
                        }}>
                            {plan.highlight && (
                                <Box sx={{
                                    position: 'absolute',
                                    top: -14,
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    bgcolor: '#FCD34D',
                                    color: '#78350F',
                                    fontWeight: 800,
                                    fontSize: '0.72rem',
                                    px: 2,
                                    py: 0.5,
                                    borderRadius: '20px',
                                    whiteSpace: 'nowrap',
                                    letterSpacing: '0.02em',
                                }}>
                                    הכי פופולרי
                                </Box>
                            )}
                            <CardContent sx={{ p: 3, textAlign: 'center' }}>
                                <Box sx={{
                                    width: 52,
                                    height: 52,
                                    borderRadius: '14px',
                                    bgcolor: plan.iconBg,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mx: 'auto',
                                    mb: 1.75,
                                }}>
                                    <Icon sx={{ color: plan.iconColor, fontSize: 28 }} />
                                </Box>
                                <Typography sx={{ fontWeight: 700, color: '#1e3a5f', fontSize: '1.1rem', mb: 0.25 }}>
                                    {displayName}
                                </Typography>
                                <Typography sx={{ color: '#94a3b8', fontWeight: 600, fontSize: '0.88rem', mb: 2 }}>
                                    {plan.price}
                                </Typography>
                                {plan.features.map(f => (
                                    <Typography key={f} sx={{
                                        color: '#475569',
                                        fontSize: '0.82rem',
                                        lineHeight: 1.9,
                                        textAlign: 'right',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.75,
                                    }}>
                                        <Box component="span" sx={{ color: plan.iconColor, fontWeight: 700, fontSize: '0.9rem' }}>✓</Box>
                                        {f}
                                    </Typography>
                                ))}
                            </CardContent>
                        </Card>
                    );
                })}
            </Box>

            {/* CTA */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, width: '100%', maxWidth: 400 }}>
                <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    onClick={() => setContactOpen(true)}
                    sx={{
                        background: 'linear-gradient(135deg, #D97706 0%, #F59E0B 100%)',
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: '1rem',
                        borderRadius: '14px',
                        px: 5,
                        py: 1.6,
                        textTransform: 'none',
                        boxShadow: '0 4px 20px rgba(217,119,6,0.4)',
                        letterSpacing: '0.01em',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #B45309 0%, #D97706 100%)',
                            boxShadow: '0 6px 24px rgba(217,119,6,0.5)',
                        }
                    }}
                >
                    שלחו לנו הודעה — נחזור מהר
                </Button>
                <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.76rem', textAlign: 'center' }}>
                    מגיבים תוך שעות ספורות · פתוחים לכל בקשה, שאלה ורעיון
                </Typography>
                <Button
                    variant="text"
                    onClick={handleLogout}
                    sx={{
                        color: 'rgba(255,255,255,0.35)',
                        fontWeight: 400,
                        fontSize: '0.82rem',
                        textTransform: 'none',
                        mt: 0.5,
                        '&:hover': { color: 'rgba(255,255,255,0.6)', bgcolor: 'transparent' }
                    }}
                >
                    התנתק
                </Button>
            </Box>
        </Box>

        <ContactDialog
            open={contactOpen}
            onClose={() => setContactOpen(false)}
            prefill={{ subject: 'שאלה לגבי המסלולים' }}
        />
        </>
    );
};

export default UpgradeScreen;
